import { REPUTATION_CHANGES } from '@/constants/financial.constants'
import {
  RANK_THRESHOLDS_WCP,
  MIN_REPUTATION_SCORE,
  MAX_REPUTATION_SCORE,
  WCP_PERIOD_MULTIPLIER,
  REPUTATION_MESSAGES,
} from '@/constants/app.constants'
import type { UserRank } from '@/types/database.types'

/**
 * İtibar puanını günceller
 */
export function calculateReputationChange(
  currentScore: number,
  action: keyof typeof REPUTATION_CHANGES
): number {
  const change = REPUTATION_CHANGES[action]
  const newScore = currentScore + change
  return Math.max(MIN_REPUTATION_SCORE, Math.min(MAX_REPUTATION_SCORE, newScore))
}

/**
 * Tamamlanan döngü sayısına ve üye/periyot ağırlığına göre WCP (Weighted Cycle Points) hesaplar
 */
export function calculateWCP(memberCount: number, cyclePeriod: 'weekly' | 'monthly'): number {
  const multiplier = cyclePeriod === 'weekly' ? WCP_PERIOD_MULTIPLIER.weekly : WCP_PERIOD_MULTIPLIER.monthly
  return memberCount * multiplier
}

/**
 * WCP'ye (Ağırlıklı Döngü Puanı) göre rütbe belirler
 */
export function calculateRank(wcp: number): UserRank {
  if (wcp >= RANK_THRESHOLDS_WCP.ustat.min) return 'ustat'
  if (wcp >= RANK_THRESHOLDS_WCP.kalfa.min) return 'kalfa'
  return 'cirak'
}

/**
 * Sonraki rütbeye ilerlemeyi hesaplar (WCP tabanlı)
 */
export function calculateRankProgress(wcp: number): {
  currentRank: UserRank
  nextRank: UserRank | null
  pointsRemaining: number
  progressPercent: number
} {
  const currentRank = calculateRank(wcp)
  if (currentRank === 'ustat') {
    return {
      currentRank,
      nextRank: null,
      pointsRemaining: 0,
      progressPercent: 100,
    }
  }

  const nextRank: UserRank = currentRank === 'cirak' ? 'kalfa' : 'ustat'
  const targetWCP = nextRank === 'kalfa' ? RANK_THRESHOLDS_WCP.kalfa.min : RANK_THRESHOLDS_WCP.ustat.min

  const currentThreshold = currentRank === 'cirak' ? 0 : RANK_THRESHOLDS_WCP.kalfa.min
  const wcpInRank = wcp - currentThreshold
  const wcpToNextRank = targetWCP - currentThreshold
  // 0'a bölme hatasına karşın
  const rawProgress = wcpToNextRank > 0 ? Math.round((wcpInRank / wcpToNextRank) * 100) : 0

  return {
    currentRank,
    nextRank,
    pointsRemaining: targetWCP - wcp,
    progressPercent: Math.min(100, Math.max(0, rawProgress)),
  }
}

/**
 * Puan kademelerine göre motivasyon mesajını döner
 */
export function getReputationMessage(score: number): { text: string; tone: string } {
  const safeScore = Math.max(0, Math.min(100, score))
  const messageObj = REPUTATION_MESSAGES.find((m) => safeScore <= m.max)
  
  // Bulunamazsa en üst kademeyi dön
  if (!messageObj) {
    return {
      text: REPUTATION_MESSAGES[REPUTATION_MESSAGES.length - 1].text,
      tone: REPUTATION_MESSAGES[REPUTATION_MESSAGES.length - 1].tone,
    }
  }

  return { text: messageObj.text, tone: messageObj.tone }
}
